export const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export const MONTHS_LONG = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
]

export const WEEKDAYS_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const WEEKDAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

const MINUS = '−'

const grouping = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 })

const trimZero = (value: string): string => value.replace(/\.0$/, '')

const toDate = (iso: string): Date | null => {
    const date = new Date(iso)

    return Number.isNaN(date.getTime()) ? null : date
}

export const formatNumber = (value: number): string => {
    const sign = value < 0 ? MINUS : ''

    return `${sign}${grouping.format(Math.abs(Math.round(value)))}`
}

export const formatCurrency = (value: number): string => {
    const sign = value < 0 ? MINUS : ''

    return `${sign}$${grouping.format(Math.abs(Math.round(value)))}`
}

export const formatCompactCurrency = (value: number): string => {
    const abs = Math.abs(value)
    const sign = value < 0 ? MINUS : ''

    if (abs >= 1_000_000) return `${sign}$${trimZero((abs / 1_000_000).toFixed(1))}M`
    if (abs >= 100_000) return `${sign}$${Math.round(abs / 1_000)}k`
    if (abs >= 1_000) return `${sign}$${trimZero((abs / 1_000).toFixed(1))}k`

    return `${sign}$${Math.round(abs)}`
}

export const formatPercent = (value: number, digits = 1): string => `${value.toFixed(digits)}%`

export const percentChange = (current: number | null, previous: number | null): number | null => {
    if (current === null || previous === null || previous === 0) return null

    return ((current - previous) / previous) * 100
}

export const formatSignedPercent = (value: number): string => {
    const abs = Math.abs(value)
    const digits = abs < 10 ? 1 : 0
    const rounded = Number(abs.toFixed(digits))

    if (rounded === 0) return '0%'

    return `${value > 0 ? '+' : MINUS}${trimZero(abs.toFixed(digits))}%`
}

export const formatSignedPoints = (value: number): string => {
    const rounded = Number(Math.abs(value).toFixed(1))

    if (rounded === 0) return '0 pts'

    return `${value > 0 ? '+' : MINUS}${rounded.toFixed(1)} pts`
}

export const formatSignedCurrency = (value: number): string => {
    if (value === 0) return '$0'

    return `${value > 0 ? '+' : MINUS}$${grouping.format(Math.abs(Math.round(value)))}`
}

export const formatShortDate = (iso: string): string => {
    const date = toDate(iso)

    if (!date) return '—'

    return `${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`
}

export const formatDate = (iso: string): string => {
    const date = toDate(iso)

    if (!date) return '—'

    return `${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`
}

export const formatTime = (iso: string): string => {
    const date = toDate(iso)

    if (!date) return '—'

    const hours = date.getUTCHours()
    const minutes = String(date.getUTCMinutes()).padStart(2, '0')
    const hour = hours % 12 === 0 ? 12 : hours % 12

    return `${hour}:${minutes} ${hours >= 12 ? 'PM' : 'AM'}`
}

export const formatDateTime = (iso: string): string => `${formatDate(iso)}, ${formatTime(iso)}`

const dayKey = (date: Date): string => date.toISOString().slice(0, 10)

export const formatDayHeading = (iso: string, nowIso: string): string => {
    const date = toDate(iso)
    const now = toDate(nowIso)

    if (!date || !now) return '—'

    const yesterday = new Date(now.getTime() - 86_400_000)

    if (dayKey(date) === dayKey(now)) return 'Today'
    if (dayKey(date) === dayKey(yesterday)) return 'Yesterday'

    const base = `${WEEKDAYS_LONG[date.getUTCDay()]}, ${MONTHS_SHORT[date.getUTCMonth()]} ${date.getUTCDate()}`

    return date.getUTCFullYear() === now.getUTCFullYear() ? base : `${base}, ${date.getUTCFullYear()}`
}

export const formatRelative = (iso: string, nowIso: string): string => {
    const date = toDate(iso)
    const now = toDate(nowIso)

    if (!date || !now) return '—'

    const seconds = Math.max(0, Math.round((now.getTime() - date.getTime()) / 1000))

    if (seconds < 60) return 'Just now'

    const minutes = Math.floor(seconds / 60)

    if (minutes < 60) return `${minutes} min ago`

    const hours = Math.floor(minutes / 60)

    if (hours < 24) return `${hours} hr ago`
    if (hours < 48) return 'Yesterday'

    const days = Math.floor(hours / 24)

    if (days < 7) return `${days} days ago`

    return date.getUTCFullYear() === now.getUTCFullYear() ? formatShortDate(iso) : formatDate(iso)
}

export const initials = (name: string): string => {
    const words = name.trim().split(/\s+/).filter(Boolean)

    if (words.length === 0) return '?'
    if (words.length === 1) return words[0].slice(0, 2).toUpperCase()

    return `${words[0][0]}${words[1][0]}`.toUpperCase()
}
